import { useState, useEffect } from "react";
import { Grid3X3, Loader2, CheckCircle2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUniqueValidation } from "@/hooks/useUniqueValidation";
import { cn } from "@/lib/utils";

interface TableFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { number: string; name?: string; capacity?: number }) => Promise<void>;
  initialData?: {
    id?: string;
    number: string;
    name: string | null;
    capacity: number;
  } | null;
}

export function TableFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
}: TableFormDialogProps) {
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("4");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!initialData;

  const { isChecking, isUnique, checkUnique, reset } = useUniqueValidation({
    table: "tables",
    column: "number",
    excludeId: initialData?.id,
  });

  useEffect(() => {
    if (open && initialData) {
      setNumber(initialData.number);
      setName(initialData.name || "");
      setCapacity(String(initialData.capacity));
      reset();
    } else if (open) {
      setNumber("");
      setName("");
      setCapacity("4");
      reset();
    }
  }, [open, initialData, reset]);

  const handleNumberChange = (value: string) => {
    setNumber(value);
    // Only validate if value is different from initial (for editing)
    if (!isEditing || value.trim() !== initialData?.number) {
      checkUnique(value);
    } else {
      reset();
    }
  };

  const handleSubmit = async () => {
    if (!number.trim()) return;
    if (isUnique === false) return; // Don't submit if duplicate

    setIsSubmitting(true);
    try {
      await onSubmit({
        number: number.trim(),
        name: name.trim() || undefined,
        capacity: parseInt(capacity) || 4,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showValidation = number.trim() && (!isEditing || number.trim() !== initialData?.number);
  const isValid = !showValidation || isUnique === true;
  const canSubmit = number.trim() && !isSubmitting && !isChecking && isValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Grid3X3 className="h-5 w-5" />
            {isEditing ? "Editar Mesa" : "Nova Mesa"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="number">Número da Mesa *</Label>
            <div className="relative">
              <Input
                id="number"
                placeholder="Ex: 01"
                value={number}
                onChange={(e) => handleNumberChange(e.target.value)}
                autoFocus
                className={cn(
                  showValidation && isUnique === false && "border-destructive focus-visible:ring-destructive",
                  showValidation && isUnique === true && "border-green-500 focus-visible:ring-green-500"
                )}
              />
              {showValidation && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isChecking ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : isUnique === true ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : isUnique === false ? (
                    <XCircle className="h-4 w-4 text-destructive" />
                  ) : null}
                </div>
              )}
            </div>
            {showValidation && isUnique === false && (
              <p className="text-sm text-destructive">
                Já existe uma mesa com este número
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome/Descrição (opcional)</Label>
            <Input
              id="name"
              placeholder="Ex: Mesa da Janela"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacidade (pessoas)</Label>
            <Input
              id="capacity"
              type="number"
              min="1"
              max="50"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? "Salvando..." : isEditing ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
